import * as React from "react";
import { Book as BookType } from "../../../types/Book";
import findBook from "../../../requests/findBook";
import "./FindBook.css";
import Book from "../../Book/Book";
import cn from "classnames";
import CloseIcon from "@material-ui/icons/Close";
import LinearProgress from "@material-ui/core/LinearProgress";
import BookDialogPreview from "../../BookDialogPreview/BookDialogPreview";
import OutsideClickHandler from "react-outside-click-handler";
import Initial from "./Initial/Initial";
import { CardsManagerCallbacks } from "../../CardsManager/CardsManager";
import SearchIcon from "@material-ui/icons/Search";
import getAutocomplete from "../../../requests/getAutocomplete";
import MediaQuery from "react-responsive";
import { MOBILE_MAX_WIDTH } from "../../../utils";

interface Props {
	callbacks: CardsManagerCallbacks;
	withoutInitial?: boolean;
	dummyRender?: (onClick: () => void) => React.ReactNode;
	onBookSelect?: (book: BookType) => void;
}

const FindBook: React.FunctionComponent<Props> = (props) => {
	const inputRef = React.useRef<HTMLInputElement>(null);

	const [fullMode, setFullMode] = React.useState<boolean>(false);
	const [addedBooks, setAddedBooks] = React.useState<BookType[]>([]);
	const [selectedBookId, setSelectedBookId] = React.useState<BookType>(null);
	const [isLoading, setLoading] = React.useState(false);
	const [autocomplete, setAutocomplete] = React.useState([]);

	const [search, setSearch] = React.useState("");

	const disableFullMode = () => {
		inputRef.current.value = "";
		setFullMode(false);
		setAddedBooks([]);
	};

	const onFocus = () => {
		setFullMode(true);
	};

	React.useEffect(() => {
		inputRef.current?.addEventListener("focus", onFocus);

		return () => {
			inputRef.current?.removeEventListener("focus", onFocus);
		};
	}, []);

	const getResults = (query: string) => {
		const addedBooksTmp: BookType[] = [],
			otherBooksTmp: BookType[] = [];

		if ((window as any)["ym"]) {
			(window as any).ym(63827389, "reachGoal", "book_search_begin", {
				bookSearch: query,
			});
		}

		inputRef.current.value = query;
		setAutocomplete([]);
		setSearch("");

		setLoading(true);

		findBook(query).then((data) => {
			const categoriesMap = new Map<string, number>();

			setAddedBooks(data);

			/*categoriesMap.forEach((count, key) => {
				categories.push({
					name: key,
					count
				})
			})*/

			setLoading(false);
		});
	};

	React.useEffect(() => {
		if (search.length > 1) {
			getAutocomplete(search).then((data) => {
				setAutocomplete(data);
			});
		}
	}, [search]);

	const onBookClick = (book: BookType) => {
		disableFullMode();

		if (props.onBookSelect) {
			props.onBookSelect(book);

			return;
		}

		if (book.woo_id) {
			window.location.href = `/book/${book.woo_id}/`;

			return;
		}
		else {
			window.location.href = `/book/t${book.externalId}/`;

			return;
		}

		if ((window as any)["ym"]) {
			(window as any).ym(63827389, "reachGoal", "book_search_bookClick", {
				bookSearch: inputRef.current?.value,
				bookName: book.image,
				bookId: book.externalId,
			});
		}

		setSelectedBookId(book);
	};

	const enableFullMode = () => {
		setFullMode(true);

		setTimeout(() => inputRef.current?.focus(), 300);

		if ((window as any)["ym"]) {
			(window as any).ym(63827389, "reachGoal", "book_search_start");
		}
	};

	const filteredBook = React.useMemo(() => {
		return addedBooks;
	}, [addedBooks]);

	return (
		<>
			{!fullMode && (
				<>
					{props.dummyRender ? (
						props.dummyRender(enableFullMode)
					) : (
						<div className="default-dummy-input" onClick={enableFullMode}>
							<SearchIcon />
							Найдите книгу, которая вас интересует
						</div>
					)}
				</>
			)}

			<div className={cn("find-book", { "find-book_fullscreen": fullMode })}>
				<OutsideClickHandler onOutsideClick={disableFullMode}>
					<div
						className="find-book__wrapper"
						style={{ display: fullMode ? "block" : "none" }}
					>
						<form className="find-book__inputWrapper" action="/q" method="GET">
							<input
								type="text"
								placeholder="Найдите книгу, которая вас интересует"
								ref={inputRef}
								autoFocus={true}
								spellCheck={false}
								autoComplete="none"
								name="s"
							/>

							{fullMode && (
								<CloseIcon
									className="find-book__close"
									fontSize="large"
									onClick={disableFullMode}
								/>
							)}
						</form>

						{fullMode && search && (
							<div className="find-book__result find-book__result_suggest">
								<div
									className="find-book-suggest__item"
									onClick={() => getResults(search)}
								>
									Искать <b>{search}</b>
								</div>

								{autocomplete.length > 0 &&
									autocomplete.map((value) => {
										return (
											<div
												className="find-book-suggest__item"
												onClick={() => getResults(value)}
												key={value}
											>
												{value}
											</div>
										);
									})}
							</div>
						)}

						{fullMode && autocomplete.length === 0 && (
							<div className="find-book__result">
								{isLoading && <LinearProgress />}

								{!inputRef.current.value && !props.withoutInitial && (
									<Initial onClickCard={disableFullMode} />
								)}

								{inputRef.current.value && !isLoading && addedBooks.length > 0 && (
									<>
										{addedBooks.length > 0 && (
											<div>
												{filteredBook.map((book, index) => {
													return (
														<Book
															className="find-book__item"
															type="standart"
															key={book.externalId}
															book={book}
															onClick={onBookClick}
														/>
													);
												})}
											</div>
										)}
									</>
								)}
							</div>
						)}

						<MediaQuery maxWidth={MOBILE_MAX_WIDTH}>
							<a
								href="https://readed.me/book-form"
								className="find-book__bookNotFound"
							>
								Не нашли книгу?
							</a>
						</MediaQuery>
					</div>
				</OutsideClickHandler>

				<BookDialogPreview
					bookExternalId={selectedBookId?.externalId}
					bookWooId={selectedBookId?.woo_id}
					onClose={() => setSelectedBookId(null)}
					onSaved={() => setSelectedBookId(null)}
					saveCard={props.callbacks.addCard}
					saveProperties={props.callbacks.saveCardProperties}
				/>
			</div>
		</>
	);
};

export default FindBook;
